import type { CmmdOrgProviderPolicy, ProviderKind, ServerProvider } from "@cmmd-forge/contracts";
import { useMemo } from "react";

import {
  providerKindForRail,
  type PickerRailProvider,
  type ShellProviderAvailability,
} from "../../cmmd/providerAvailabilityJoin";
import { describeProviderAvailability } from "../../cmmd/providerIneligibilityCopy";
import type { ModelPickerProviderAvailability } from "./modelPickerProviderAvailability";
import { resolveModelPickerRailRows, type ModelPickerRailRow } from "./modelPickerRail";

/**
 * The rail the picker draws, and what each row is allowed to do.
 *
 * The rail was a hardcoded set of five provider kinds, so a provider an
 * Environment published had no button and every withheld one arrived as an
 * absence the picker had to interpret. It is derived now; see
 * `docs/adr/withheld-providers-carry-their-reason.md`.
 */
export function useModelPickerRail(input: {
  readonly providers: ReadonlyArray<ServerProvider> | undefined;
  readonly policy: CmmdOrgProviderPolicy | null | undefined;
  readonly availability: ReadonlyArray<ModelPickerProviderAvailability> | undefined;
}): {
  readonly rows: ReadonlyArray<ModelPickerRailRow>;
  readonly availabilityByProvider: ReadonlyMap<PickerRailProvider, ShellProviderAvailability>;
  /**
   * The provider kinds with a rail row. A model whose provider has no row has
   * nowhere to be shown, greyed or otherwise.
   */
  readonly railProviderKinds: ReadonlySet<ProviderKind>;
} {
  const { availability, policy, providers } = input;
  const rows = useMemo(
    () => resolveModelPickerRailRows({ providers, policy, availability }),
    [availability, policy, providers],
  );
  const availabilityByProvider = useMemo(
    () => new Map(rows.map((row) => [row.provider, row.availability] as const)),
    [rows],
  );
  const railProviderKinds = useMemo(() => {
    const kinds = new Set<ProviderKind>();
    for (const row of rows) {
      const kind = providerKindForRail(row.provider);
      if (kind !== null) kinds.add(kind);
    }
    return kinds;
  }, [rows]);
  return { rows, availabilityByProvider, railProviderKinds };
}

/** The sentence the search header shows for the provider the rail has open. */
export function describeSelectedRailProvider(
  selectedProvider: ProviderKind | "favorites",
  availabilityByProvider: ReadonlyMap<PickerRailProvider, ShellProviderAvailability>,
): string | null {
  if (selectedProvider === "favorites") return null;
  const availability = availabilityByProvider.get(selectedProvider);
  return availability === undefined
    ? null
    : describeProviderAvailability(selectedProvider, availability);
}
